import React from "react";
import { Card, CardBody } from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";

const OrderTrackingCard = ({ orderDetail, orderEvents, title, className }) => {
  const { t } = useTranslation();

  if (!orderEvents || !orderEvents.length) {
    return (
      <Card className={`shadow border-0 mb-3 ${className || ""}`}>
        <CardBody className="text-center text-muted p-4">
          <h5 className="mb-2 heading-md text-primary font-weight-bold">
            {title || t("page.orders.tracking.title", "Order Tracking")}
          </h5>
          <p className="mb-0">{t("page.orders.tracking.noUpdates", "No tracking updates available for this order yet.")}</p>
        </CardBody>
      </Card>
    );
  }

  const sortedEvents = [...orderEvents].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
  const norm = (s) => (s || "").toLowerCase().replace(/[\s_-]+/g, "");

  let hasCancelled = false;
  let latestFailedEvent = null;
  let hasDelivered = false;
  let pickedUpEvent = null;
  let readyForPickupEvent = null;
  let cancelledEvent = null;

  sortedEvents.forEach((ev) => {
    const evNorm = norm(ev.event_type);
    if (evNorm === "cancelled") {
      hasCancelled = true;
      cancelledEvent = ev;
    }
    if (evNorm === "failed") latestFailedEvent = ev;
    if (evNorm === "delivered") hasDelivered = true;
    if (evNorm === "pickedup") pickedUpEvent = ev;
    if (evNorm === "readyforpickup") readyForPickupEvent = ev;
  });

  const isTerminal = orderDetail?.status === "Cancelled" || orderDetail?.status === "Failed" || hasCancelled || !!latestFailedEvent;
  const wasPickedUp = !!pickedUpEvent || orderDetail?.status === "Picked Up" || orderDetail?.status === "Delivered";
  const wasReadyForPickup = !!readyForPickupEvent || wasPickedUp || orderDetail?.status === "Ready for Pickup";

  const statusOrder = ["Confirmed"];
  if (wasReadyForPickup || !isTerminal) {
    statusOrder.push("Ready for Pickup");
  }
  if (wasPickedUp || !isTerminal) {
    statusOrder.push("Picked Up");
  }
  if (wasPickedUp || !isTerminal) {
    statusOrder.push("Expected Arrival");
  }

  const extraStatuses = [];
  if (hasDelivered || orderDetail?.status === "Delivered") {
    extraStatuses.push("Delivered");
  } else {
    if (latestFailedEvent || orderDetail?.status === "Failed") extraStatuses.push("Failed");
    if (hasCancelled || orderDetail?.status === "Cancelled") extraStatuses.push("Cancelled");
  }

  const finalStatusOrder = [...statusOrder, ...extraStatuses];
  const happyPath = ["Confirmed", "Ready for Pickup", "Picked Up", "Expected Arrival", "Delivered"];
  const currentStatus = orderDetail?.status || "Confirmed";
  const currentHappyIndex = happyPath.indexOf(currentStatus);

  const getIsDone = (statusName) => {
    if (statusName === currentStatus) return true;
    if (happyPath.includes(statusName)) {
      if (currentHappyIndex !== -1 && happyPath.indexOf(statusName) <= currentHappyIndex) {
        return true;
      }
      if (statusName === "Confirmed") return true;
      if (statusName === "Ready for Pickup") return wasReadyForPickup;
      if (statusName === "Picked Up") return wasPickedUp;
      if (statusName === "Expected Arrival") return !!orderDetail?.expectedArrivalAt || currentStatus === "Delivered";
      return false;
    }
    return sortedEvents.some((e) => norm(e.event_type) === norm(statusName));
  };

  const showFailedReason = (latestFailedEvent || orderDetail?.status === "Failed") && currentStatus === "Failed";
  const rawCancellationText = orderDetail?.cancellationReason || (cancelledEvent && typeof cancelledEvent === "object" ? cancelledEvent.message : "");
  const cancellationText = typeof rawCancellationText === "string" ? rawCancellationText : String(rawCancellationText || "");
  const showCancelledReason = (hasCancelled || orderDetail?.status === "Cancelled") && currentStatus === "Cancelled" && !!cancellationText;

  const getCancellerName = () => {
    if (!cancelledEvent || !cancelledEvent.changed_by) return null;
    if (typeof cancelledEvent.changed_by === "object") {
      const name = `${cancelledEvent.changed_by.firstName || ""} ${cancelledEvent.changed_by.lastName || ""}`.trim();
      if (name) return name;
      if (cancelledEvent.changed_by.email) return cancelledEvent.changed_by.email;
    }
    return null;
  };

  const cancellerName = getCancellerName();

  return (
    <Card className={`shadow border-0 mb-3 ${className || ""}`}>
      <CardBody className="p-4">
        <h5 className="mb-4 heading-md font-weight-bold">
          {title || t("page.orders.tracking.title", "Order Tracking")}
        </h5>

        <div className="d-flex flex-column pl-2">
          {finalStatusOrder.map((status, index) => {
            const isDone = getIsDone(status);
            const isCurrent = status === currentStatus;

            const isFailedDot = status === "Failed";
            const isCancelledDot = status === "Cancelled";

            let eventTime =
              status === "Confirmed" && orderDetail
                ? orderDetail.createdAt
                : status === "Ready for Pickup"
                ? sortedEvents.find((e) => norm(e.event_type) === "readyforpickup")?.createdAt ||
                  orderDetail?.readyForPickupAt ||
                  null
                : status === "Picked Up"
                ? sortedEvents.find((e) => norm(e.event_type) === "pickedup")?.createdAt ||
                  orderDetail?.pickupConfirmedAt ||
                  null
                : status === "Expected Arrival"
                ? orderDetail?.expectedArrivalAt ||
                  sortedEvents.find((e) => norm(e.event_type) === "expectedarrival")?.createdAt ||
                  null
                : sortedEvents.find((e) => norm(e.event_type) === norm(status))?.createdAt;

            if (isFailedDot && latestFailedEvent) {
              eventTime = latestFailedEvent.createdAt;
            }

            let dotClass = "tracking-dot";
            if (isDone) {
              if (isFailedDot) dotClass += " status-failed";
              else if (isCancelledDot) dotClass += " status-cancelled";
              else if (isCurrent && status === "Ready for Pickup") dotClass += " status-warning";
              else dotClass += " status-done";
            }

            // The connector line between this step and the NEXT step is green only
            // if the NEXT step is also done (i.e., we've moved past this point).
            const nextStatus = finalStatusOrder[index + 1];
            const nextIsDone = nextStatus ? getIsDone(nextStatus) : false;
            let lineClass = `tracking-line ${nextIsDone ? "status-done" : ""}`;

            let textColorClass = isDone 
              ? (isFailedDot ? "text-danger" : isCancelledDot ? "text-muted" : (isCurrent && status === "Ready for Pickup") ? "text-warning" : "text-success") 
              : "text-muted";

            const expectedDate = orderDetail?.sender_listing_id?.date || orderDetail?.traveler_listing_id?.date;

            const formattedTimeStr =
              status === "Expected Arrival"
                ? orderDetail?.expectedArrivalAt
                  ? helpers.formatExpectedArrival(orderDetail.expectedArrivalAt, orderDetail.expectedArrivalTimezone)
                  : null
                : eventTime
                ? helpers.getFormattedDate(eventTime, t)
                : !wasPickedUp && status === "Picked Up" && expectedDate
                ? `${t("page.messages.expected", "Expected")}: ${helpers.getFormattedDate(expectedDate, t)}`
                : null;

            return (
              <div key={status} className="d-flex align-items-start mb-0">
                <div className="d-flex flex-column align-items-center mr-3 mt-1">
                  <div className={dotClass} />
                  {index !== finalStatusOrder.length - 1 && (
                    <div className={lineClass} />
                  )}
                </div>

                <div className="d-flex flex-column pb-4">
                  <div className={`font-weight-600 text-sm mb-1 ${textColorClass}`}>
                    {t(`page.orders.status.${helpers.normalizeOrderStatus ? helpers.normalizeOrderStatus(status) : norm(status)}`, status)}
                  </div>
                  {formattedTimeStr && (
                    <div className="text-xs text-muted">
                      {formattedTimeStr}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {showFailedReason && latestFailedEvent && (
            <div className="mt-2 p-3 bg-secondary border border-light rounded">
              <div className="font-weight-bold text-danger text-sm mb-1">
                {t("page.orders.tracking.failureReason", "Failure Reason")}:
              </div>
              <div className="text-sm text-muted">
                {latestFailedEvent.message} (
                {helpers.getFormattedDate(latestFailedEvent.createdAt, t)})
              </div>
            </div>
          )}

          {showCancelledReason && (
            <div className="mt-2 p-3 bg-secondary border border-light rounded">
              <div className="font-weight-bold text-dark text-sm mb-1">
                {t("page.orders.cancellationReasonTitle", "Cancellation Reason")}:
              </div>
              <div className="text-sm text-muted mb-1">
                {cancellationText}
              </div>
              <div className="text-xs text-muted">
                {cancellerName && (
                  <span className="font-weight-600 mr-2 text-dark">
                    {t("page.orders.cancelledBy", "Cancelled by")}: {cancellerName}
                  </span>
                )}
                {cancelledEvent && typeof cancelledEvent === "object" && cancelledEvent.createdAt && (
                  <span>({helpers.getFormattedDate(cancelledEvent.createdAt, t)})</span>
                )}
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default OrderTrackingCard;